import { Box, Button, ButtonGroup, CircularProgress, Grid, Typography } from "@mui/material";
import InboxIcon from '@mui/icons-material/Inbox'
import TrendIcon from '@mui/icons-material/TrendingUp'
import React, { useEffect } from "react";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";

import {
    callAPI,
    getLocalValue,
    LoacalVariables,
    Method
} from "../../commonFunctions/CommonFunctions";
import { IHeaderActions } from "../../Components/Header/Header";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { Helmet } from "react-helmet";
import { CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, LineChart } from "recharts";


const groupOptions = ["day", "week", "month", "year"];

// Define the types for the loan data
type LoanTrendItem = {
    label: string;
    count: number;
    totalAmount?: number;
};

type LoanTrendApiResponse = {
    _id: {
        year: number;
        month?: number;
        day?: number;
        week?: number;
    };
    count: number;
    totalAmount: number;
};

const AllLoans: React.FC<any> = React.memo(() => {
    const { showMessage } = useSnackbar();
    const navigate = useNavigate();

    const userType = getLocalValue(LoacalVariables.UserType);

    const [districtOptions, setDistrictOptions] = React.useState<any[]>([]);
    const [blockOptions, setBlockOptions] = React.useState<any[]>([]);
    const [cdsOptions, setCdsOptions] = React.useState<any[]>([]);

    const [groupBy, setGroupBy] = React.useState("month");
    const [loading, setLoading] = React.useState(false);
    const [loanData, setLoanData] = React.useState<LoanTrendItem[]>([]);
    const [intrestData, setIntrestData] = React.useState<LoanTrendItem[]>([]);

    const fetchLoanTrend = async (group = "month") => {
        setLoading(true);
        try {
            const res = await callAPI(`dashboards/loan/trend?groupBy=${group}`, Method.GET);

            // Map the response to the LoanTrendItem type
            const formatted = res.data.map((item: LoanTrendApiResponse) => {
                const { _id, count, totalAmount } = item;
                let label = "";

                // Construct the label based on the available properties
                if (_id.day !== undefined) label = `${_id.day}/${_id.month}/${_id.year}`;
                else if (_id.week !== undefined) label = `W${_id.week} ${_id.year}`;
                else if (_id.month !== undefined) label = `${_id.month}/${_id.year}`;
                else label = `${_id.year}`;

                return { label, count, totalAmount };
            });
            setLoanData(formatted);
        } catch (err) {
            console.error("Error loading trend", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchIntresetTrend = async (group = "month") => {
        setLoading(true);
        try {
            const res = await callAPI(`dashboards/intreset/trend?groupBy=${group}`, Method.GET);

            // Map the response to the LoanTrendItem type
            const formatted = res.data.map((item: LoanTrendApiResponse) => {
                const { _id, count, totalAmount } = item;
                let label = "";

                // Construct the label based on the available properties
                if (_id.day !== undefined) label = `${_id.day}/${_id.month}/${_id.year}`;
                else if (_id.week !== undefined) label = `W${_id.week} ${_id.year}`;
                else if (_id.month !== undefined) label = `${_id.month}/${_id.year}`;
                else label = `${_id.year}`;

                return { label, count, totalAmount };
            });
            setIntrestData(formatted);
        } catch (err) {
            console.error("Error loading trend", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoanTrend(groupBy);
        // fetchIntresetTrend(groupBy);
    }, [groupBy]);


    const source = {
        url: "loans/search",
        page: 1,
        pageSize: 10,
        filter: [],
        sort: []
    };

    useEffect(() => {
        if (userType === "admin") {
            fetchDistricts();
            fetchBlocks();
            fetchCDS();
        } else if (userType === "district") {
            const districtId = getLocalValue(LoacalVariables.District);
            if (districtId) {
                fetchBlocks(districtId);
            }
        }
    }, []);

    const fetchDistricts = async () => {
        try {
            const res = await callAPI("districts/getAll", Method.GET);
            setDistrictOptions(
                res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch districts:", error);
            showMessage("Failed to load districts", "error");
        }
    };

    const fetchBlocks = async (districtId?: string) => {
        try {
            // setLoadingRegion(true);
            // const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
            const res = await callAPI('blocks/getAll', Method.GET);

            setBlockOptions(
                res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch blocks:", error);
            showMessage("Failed to load blocks", "error");
        } finally {
            // setLoadingRegion(false);
        }
    };

    const fetchCDS = async (blockId?: string) => {
        try {
            // const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);
            const res = await callAPI('cds/getAll', Method.GET);

            setCdsOptions(
                res?.data.map((c: any) => ({ label: c.name, value: c._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch CDS:", error);
            showMessage("Failed to load CDS", "error");
        }
    };

    const handleClick = () => {
        navigate('/loan/request')
    };


    const columns: IColumns[] = [
        {
            header: "Loan ID",
            fieldName: "loanId",
            tooltip: true,
            sortable: true
        },
        {
            header: "Applicant Name",
            fieldName: "applicantName",
            tooltip: true,
            sortable: true
        },
        {
            header: "Enterprise Name",
            fieldName: "enterpriseName",
            tooltip: true,
            sortable: true
        },
        {
            header: "Mobile Number",
            fieldName: "mobile",
            tooltip: true
        },

        {
            header: "Applied Amount",
            fieldName: "appliedAmount",
            tooltip: true,
            sortable: true,
            type: "currency"
        },

        {
            header: userType === 'district' || userType === "admin" ? "Block" : "CDS",
            fieldName: userType === 'district' || userType === "admin" ? "block.name" : "cds.name",
            tooltip: true,
            type: "default"
        },

        {
            header: "Status",
            fieldName: "status",
            tooltip: true,
            type: "status"
        },
        // {
        //     header: "Actions",
        //     fieldName: "actions",
        //     type: "action",
        //     actions: limitedActions,
        //     actionCallback: (action, row) => handleAction(action, row)
        // }
    ];

    const filterFields = [
        { type: "text", fieldName: "loanId", label: "Loan ID" },
        { type: "text", fieldName: "applicantName", label: "Applicant Name", operator: "regex" },
        { type: "text", fieldName: "secondApplicantName", label: "Second Applicant Name" },
        { type: "text", fieldName: "mobile", label: "Mobile Number", operator: "regex" },
        { type: "text", fieldName: "whatsappNumber", label: "WhatsApp Number", operator: "regex" },
        { type: "text", fieldName: "enterpriseName", label: "Enterprise Name", operator: "regex" },
        { type: "text", fieldName: "referenceNumber", label: "Reference Number", operator: "eq" },

        { type: "dropdown", fieldName: "block", label: "Block", options: blockOptions },
        { type: "dropdown", fieldName: "cds", label: "CDS", options: cdsOptions },
    ];


    const action: IHeaderActions = {
        type: "button",
        text: "+ New Loan Request",
        size: "medium",
        onClick: handleClick,
        variant: "contained",
        color: "primary",
        sx: {
            backgroundColor: "#A71A68",
            textTransform: "none",
            "&:hover": { backgroundColor: "#b94886" }
        }
    };

    return (
        <>
            <Helmet>
                <title>All Loans | Willsoar Technologies</title>
                <meta
                    name="description"
                    content="Manage all loans in one place. View, add, or edit loan information easily."
                />
            </Helmet>
            <Grid p={2}>
                {/* {userType === "block" ? <Header
                    type="new"
                    title="All Loans"
                    id={"loanHeader"}
                    action={action}
                    icon={<WSIcons.Document fontSize="medium" color="#7D79C2" />}
                /> : <Header
                    type="new"
                    title="All Loans"
                    id={"loanHeader"}
                    icon={<WSIcons.Document fontSize="medium" color="#7D79C2" />}
                />} */}

                <Typography variant="h6" gutterBottom> All Loans</Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <WSDataGrid
                            id="loanGrid"
                            columns={columns}
                            source={source}
                            filterFields={filterFields}
                            searchBox={{ fieldName: "mobile", type: "number" }}
                            placeholder={"Search by Mobile Number"}
                            exportCsv
                            buttonStyle="Sigma"
                        />
                    </Grid>
                </Grid>

                <Box mt={4} mb={8} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            background: 'linear-gradient(90deg, #4DD26B 0%, #08B1B6 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'inline-block'
                        }}>
                            <TrendIcon /> Loan Trends Analysis
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                                Group by:
                            </Typography>
                            <ButtonGroup variant="outlined" size="small">
                                {groupOptions.map(option => (
                                    <Button
                                        key={option}
                                        variant={groupBy === option ? "contained" : "outlined"}
                                        onClick={() => setGroupBy(option)}
                                        sx={{
                                            backgroundColor: groupBy === option ? '#4DD26B' : 'transparent',
                                            color: groupBy === option ? 'white' : '#4DD26B',
                                            textTransform: 'none',
                                            fontWeight: groupBy === option ? 600 : 400,
                                            borderColor: '#4DD26B',
                                            px: 2,
                                            '&:hover': {
                                                backgroundColor: groupBy === option ? '#08B1B6' : 'action.hover',
                                                borderColor: '#08B1B6',
                                            },
                                        }}
                                    >
                                        {option.charAt(0).toUpperCase() + option.slice(1)}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <CircularProgress
                                    size={60}
                                    thickness={4}
                                    sx={{
                                        color: '#4DD26B',
                                        mb: 2
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Loading loan data...
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            backgroundColor: '#f8fafc',
                            borderRadius: 2,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart
                                    data={loanData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                >
                                    <defs>
                                        <linearGradient id="countGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3D52A0" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#3D52A0" stopOpacity={0.2} />
                                        </linearGradient>
                                        <linearGradient id="amountGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#7091E6" stopOpacity={0.8} />
                                            <stop offset="95%" stopColor="#7091E6" stopOpacity={0.2} />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        vertical={false}
                                        stroke="#e2e8f0"
                                    />

                                    <XAxis
                                        dataKey="label"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />

                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                        tickFormatter={(value) => value.toLocaleString()}
                                    />

                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            borderRadius: 8,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                            border: '1px solid #e2e8f0'
                                        }}
                                        labelStyle={{
                                            color: '#1e293b',
                                            fontWeight: 600,
                                            marginBottom: 8
                                        }}
                                        itemStyle={{
                                            color: '#64748b',
                                            fontSize: 13
                                        }}
                                        formatter={(value, name) => {
                                            if (name === 'Total Amount') {
                                                return [`₹${Number(value).toLocaleString()}`, name];
                                            }
                                            return [value, name];
                                        }}
                                    />

                                    <Legend
                                        verticalAlign="top"
                                        align="right"
                                        iconType="circle"
                                        iconSize={10}
                                        wrapperStyle={{ paddingBottom: 20 }}
                                    />

                                    <Line
                                        type="monotone"
                                        dataKey="count"
                                        name="Loan Count"
                                        stroke="#3D52A0"
                                        strokeWidth={3}
                                        dot={{ fill: '#3D52A0', strokeWidth: 2, r: 4 }}
                                        activeDot={{ r: 7, fill: '#3D52A0', stroke: '#fff', strokeWidth: 2 }}
                                    />

                                    <Line
                                        type="monotone"
                                        dataKey="totalAmount"
                                        name="Total Amount (₹)"
                                        stroke="#7091E6"
                                        strokeWidth={3}
                                        dot={{ fill: '#7091E6', strokeWidth: 2, r: 4 }}
                                        activeDot={{ r: 7, fill: '#7091E6', stroke: '#fff', strokeWidth: 2 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    )}

                    {!loading && loanData && loanData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 300,
                            flexDirection: 'column',
                            color: 'text.secondary'
                        }}>
                            <InboxIcon sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
                            <Typography variant="h6">
                                No data available
                            </Typography>
                            <Typography variant="body2">
                                Try selecting a different time period or grouping
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Grid>
        </>
    );
});


export default AllLoans;